import { Equipo, EquipoJSON } from '../Equipo/Equipo';
import { Arbitro, ArbitroJSON } from '../Arbitro/Arbitro';
import { CampoJuego, CampoJuegoJSON } from '../CampoJuego/CampoJuego';
import { Torneo, TorneoJSON } from '../Torneo/Torneo';
import { DateTime } from 'luxon';
import { Fase, FaseJSON } from '../Fase/Fase';

export interface PartidoJSON {
  par_id: string | null;
  par_numero_fecha: number;
  par_fecha: Date;
  par_horario: string;
  par_cjid: number | null;
  par_eqid_local: number | null;
  par_eqid_visitante: number | null;
  par_arbid_1: number | null;
  par_arbid_2: number | null;
  par_goles_local: number | null;
  par_goles_visitante: number | null;
  par_estado: string;
  par_validado: boolean;
  par_arbid_valida: number | null;
  par_local_presente: boolean;
  par_visitante_presente: boolean;
  fase: FaseJSON | null;
  local: EquipoJSON | null;
  visitante: EquipoJSON | null;
  campoJuego?: CampoJuegoJSON;
  arbitro1?: ArbitroJSON;
  arbitro2?: ArbitroJSON;
  par_local_puntos_tabla: number | null;
  par_visitante_puntos_tabla: number | null;
  par_local_puntos_tabla_general: number | null;
  par_visitante_puntos_tabla_general: number | null;
  par_extra: boolean;
  par_zona: string | null;
  par_faseid: number | null;
  par_fairplay_local: number;
  par_fairplay_visitante: number;
}

export class Partido {
  protected constructor(
    private id: string | null,
    private numeroFecha: number,
    private fecha: Date,
    private horario: string,
    private cjid: number | null,
    private eqidLocal: number | null,
    private eqidVisitante: number | null,
    private arbid1: number | null,
    private arbid2: number | null,
    private golesLocal: number | null,
    private golesVisitante: number | null,
    private estado: string,
    private validado: boolean,
    private arbidValida: number | null,
    private localPresente: boolean,
    private visitantePresente: boolean,
    private fase: Fase | null,
    private local: Equipo | null,
    private visitante: Equipo | null,
    private campoJuego: CampoJuego | null,
    private arbitro1: Arbitro | null,
    private arbitro2: Arbitro | null,
    private localPuntosTabla: number | null,
    private visitantePuntosTabla: number | null,
    private localPuntosTablaGeneral: number | null,
    private visitantePuntosTablaGeneral: number | null,
    private extra: boolean,
    private zona: string | null,
    private faseid: number | null,
    private fairplayLocal: number,
    private fairplayVisitante: number
  ) {}

  static createFrom(
    id: string | null,
    numeroFecha: number,
    fecha: Date,
    horario: string,
    cjid: number | null,
    eqidLocal: number | null,
    eqidVisitante: number | null,
    arbid1: number | null,
    arbid2: number | null,
    golesLocal: number | null,
    golesVisitante: number | null,
    estado: string,
    validado: boolean,
    arbidValida: number | null,
    localPresente: boolean,
    visitantePresente: boolean,
    fase: Fase | null,
    local: Equipo | null,
    visitante: Equipo | null,
    campoJuego: CampoJuego | null,
    arbitro1: Arbitro | null,
    arbitro2: Arbitro | null,
    localPuntosTabla: number | null,
    visitantePuntosTabla: number | null,
    localPuntosTablaGeneral: number | null,
    visitantePuntosTablaGeneral: number | null,
    extra: boolean,
    zona: string | null,
    faseid: number | null,
    fairplayLocal: number,
    fairplayVisitante: number
  ) {
    return new this(
      id,
      numeroFecha,
      fecha,
      horario,
      cjid,
      eqidLocal,
      eqidVisitante,
      arbid1,
      arbid2,
      golesLocal,
      golesVisitante,
      estado,
      validado,
      arbidValida,
      localPresente,
      visitantePresente,
      fase,
      local,
      visitante,
      campoJuego,
      arbitro1,
      arbitro2,
      localPuntosTabla,
      visitantePuntosTabla,
      localPuntosTablaGeneral,
      visitantePuntosTablaGeneral,
      extra,
      zona,
      faseid,
      fairplayLocal,
      fairplayVisitante
    );
  }

  isIdentifiedBy(anId: string): boolean {
    return this.id === anId;
  }

  isIdentifiedAs(aEntity: Partido) {
    if (this.id) {
      return aEntity.isIdentifiedBy(this.id);
    } else {
      return false;
    }
  }

  static fromJSON(entity: PartidoJSON): Partido {
    return Partido.createFrom(
      entity.par_id,
      entity.par_numero_fecha,
      new Date(entity.par_fecha),
      entity.par_horario,
      entity.par_cjid,
      entity.par_eqid_local,
      entity.par_eqid_visitante,
      entity.par_arbid_1,
      entity.par_arbid_2,
      entity.par_goles_local,
      entity.par_goles_visitante,
      entity.par_estado,
      entity.par_validado,
      entity.par_arbid_valida,
      entity.par_local_presente,
      entity.par_visitante_presente,
      entity.fase ? Fase.fromJSON(entity.fase) : null,
      entity.local ? Equipo.fromJSON(entity.local) : null,
      entity.visitante ? Equipo.fromJSON(entity.visitante) : null,
      entity.campoJuego ? CampoJuego.fromJSON(entity.campoJuego) : null,
      entity.arbitro1 ? Arbitro.fromJSON(entity.arbitro1) : null,
      entity.arbitro2 ? Arbitro.fromJSON(entity.arbitro2) : null,
      entity.par_local_puntos_tabla,
      entity.par_visitante_puntos_tabla,
      entity.par_local_puntos_tabla_general,
      entity.par_visitante_puntos_tabla_general,
      entity.par_extra,
      entity.par_zona,
      entity.par_faseid,
      entity.par_fairplay_local,
      entity.par_fairplay_visitante
    );
  }

  toJSON(): PartidoJSON {
    return {
      par_id: this.getId(),
      par_numero_fecha: this.getNumeroFecha(),
      par_fecha: this.getFecha(),
      par_horario: this.getHorario(),
      par_cjid: this.getCjid(),
      par_eqid_local: this.getEqidLocal(),
      par_eqid_visitante: this.getEqidVisitante(),
      par_arbid_1: this.getArbid1(),
      par_arbid_2: this.getArbid2(),
      par_goles_local: this.getGolesLocal(),
      par_goles_visitante: this.getGolesVisitante(),
      par_estado: this.getEstado(),
      par_validado: this.getValidado(),
      par_arbid_valida: this.getArbidValida(),
      par_local_presente: this.getLocalPresente(),
      par_visitante_presente: this.getVisitantePresente(),
      fase: this.getFase() ? this.getFase()!.toJSON() : null,
      local: this.getLocal() ? this.getLocal()!.toJSON() : null,
      visitante: this.getVisitante() ? this.getVisitante()!.toJSON() : null,
      arbitro1: this.getArbitro1()?.toJSON(),
      arbitro2: this.getArbitro2()?.toJSON(),
      campoJuego: this.getCampoJuego()?.toJSON(),
      par_local_puntos_tabla: this.getLocalPuntosTabla(),
      par_visitante_puntos_tabla: this.getVisitantePuntosTabla(),
      par_local_puntos_tabla_general: this.getLocalPuntosTablaGeneral(),
      par_visitante_puntos_tabla_general: this.getVisitantePuntosTablaGeneral(),
      par_extra: this.getExtra(),
      par_zona: this.getZona(),
      par_faseid: this.getFaseid(),
      par_fairplay_local: this.getFairplayLocal(),
      par_fairplay_visitante: this.getFairplayVisitante(),
    };
  }

  getFairplayLocal() {
    return this.fairplayLocal;
  }
  getFairplayVisitante() {
    return this.fairplayVisitante;
  }

  canEditConfiguration() {
    return this.estado == 'Disponible';
  }

  getId(): string | null {
    return this.id;
  }

  getTorid(): number {
    return +this.fase!.getTorid();
  }

  getNumeroFecha(): number {
    return this.numeroFecha;
  }

  getFecha(): Date {
    return this.fecha;
  }

  getFechaCompleta(): Date {
    const dmy_hms = DateTime.fromJSDate(this.fecha).toFormat('dd/LL/yyyy') + ' ' + this.horario;
    return DateTime.fromFormat(dmy_hms, 'dd/LL/yyyy HH:mm:ss').toJSDate();
  }

  getHorario(): string {
    let splitted = this.horario.split(':');
    return `${splitted[0]}:${splitted[1]}`;
  }

  getCjid(): number | null {
    return this.cjid;
  }

  getEqidLocal() {
    return this.eqidLocal;
  }

  getEqidVisitante() {
    return this.eqidVisitante;
  }

  getArbid1(): number | null {
    return this.arbid1;
  }

  getArbid2(): number | null {
    return this.arbid2;
  }

  getGolesLocal(): number | null {
    return this.golesLocal;
  }

  getFase() {
    return this.fase;
  }
  getGolesVisitante(): number | null {
    return this.golesVisitante;
  }

  getEstado(): string {
    return this.estado;
  }

  getValidado(): boolean {
    return this.validado;
  }

  getArbidValida(): number | null {
    return this.arbidValida;
  }

  getLocalPresente(): boolean {
    return this.localPresente;
  }

  getVisitantePresente(): boolean {
    return this.visitantePresente;
  }

  getLocal() {
    return this.local;
  }

  getVisitante() {
    return this.visitante;
  }

  getArbitro1() {
    return this.arbitro1;
  }

  getArbitro2() {
    return this.arbitro2;
  }

  getCampoJuego() {
    return this.campoJuego;
  }

  getTorneo() {
    return this.fase!.getTorneo();
  }

  getExtra() {
    return this.extra;
  }

  getZona(): string | null {
    return this.zona;
  }
  getFaseid() {
    return this.faseid;
  }

  estaFinalizado() {
    return this.estado === 'Finalizado';
  }

  estaProgramado() {
    return this.estado === 'Programado';
  }

  estaDisponible() {
    return this.estado === 'Disponible';
  }

  estaSuspendido() {
    return this.estado === 'Suspendido';
  }

  estaReprogramado() {
    return this.estado === 'Reprogramado';
  }
  estaPendienteAsignacionEquipos() {
    return this.estado === 'Sin equipo asignado';
  }

  getLocalPuntosTabla(): number | null {
    return this.localPuntosTabla;
  }

  getVisitantePuntosTabla(): number | null {
    return this.visitantePuntosTabla;
  }

  getLocalPuntosTablaGeneral(): number | null {
    return this.localPuntosTablaGeneral;
  }

  getVisitantePuntosTablaGeneral(): number | null {
    return this.visitantePuntosTablaGeneral;
  }

  setId(value: string | null): void {
    this.id = value;
  }

  setNumeroFecha(value: number): void {
    this.numeroFecha = value;
  }

  setFecha(value: Date): void {
    this.fecha = value;
  }

  setHorario(value: string): void {
    this.horario = value;
  }

  setCjid(value: number): void {
    this.cjid = value;
  }

  setEqidLocal(value: number): void {
    this.eqidLocal = value;
  }

  setEqidVisitante(value: number): void {
    this.eqidVisitante = value;
  }

  setArbid1(value: number): void {
    this.arbid1 = value;
  }

  setArbid2(value: number): void {
    this.arbid2 = value;
  }

  setGolesLocal(value: number): void {
    this.golesLocal = value;
  }

  setGolesVisitante(value: number): void {
    this.golesVisitante = value;
  }

  setEstado(value: string): void {
    this.estado = value;
  }

  setValidado(value: boolean): void {
    this.validado = value;
  }

  setArbidValida(value: number): void {
    this.arbidValida = value;
  }

  setLocalPresente(value: boolean): void {
    this.localPresente = value;
  }

  setVisitantePresente(value: boolean): void {
    this.visitantePresente = value;
  }

  getHoraInicioInDate() {
    const fyhString = DateTime.fromJSDate(this.fecha).toFormat('dd/LL/yyyy') + ' ' + this.horario;
    return DateTime.fromFormat(fyhString, 'dd/LL/yyyy HH:mm:ss').toJSDate();
  }

  getPartidoTorneoInfo() {
    const nombreTorneo = this.getTorneo()?.getNombre();
    const categoria = ` - ${this.getTorneo()?.getCategoria()?.getNombre()} (${this.getTorneo()?.getRama()})`;
    const division = this.getTorneo()?.getDivision()
      ? ' - ' + this.getTorneo()!.getDivision()!.getNombre()
      : '';
    return nombreTorneo + categoria + division;
  }

  getCopy() {
    let ret = new Partido(
      this.id,
      this.numeroFecha,
      this.fecha,
      this.horario,
      this.cjid,
      this.eqidLocal,
      this.eqidVisitante,
      this.arbid1,
      this.arbid2,
      this.golesLocal,
      this.golesVisitante,
      this.estado,
      this.validado,
      this.arbidValida,
      this.localPresente,
      this.visitantePresente,
      this.fase,
      this.local,
      this.visitante,
      this.campoJuego,
      this.arbitro1,
      this.arbitro2,
      this.getLocalPuntosTabla(),
      this.getVisitantePuntosTabla(),
      this.getLocalPuntosTablaGeneral(),
      this.getVisitantePuntosTablaGeneral(),
      this.getExtra(),
      this.zona,
      this.faseid,
      this.fairplayLocal,
      this.fairplayVisitante
    );
    return ret;
  }
}
